// frontend/src/components/NewFolderModal.jsx
import React, { useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import { createFolder } from "../api/folders";

/**
 * Flatten a folder tree into "— "-indented options for the parent selector.
 * @param {Array} nodes
 * @param {number} depth
 * @returns {Array<{id, label}>}
 */
function flattenFolders(nodes, depth = 0) {
  const out = [];
  for (const n of nodes) {
    out.push({ id: n.id, label: `${"— ".repeat(depth)}${n.name}` });
    if (n.children && n.children.length) {
      out.push(...flattenFolders(n.children, depth + 1));
    }
  }
  return out;
}

/**
 * NewFolderModal — name field + optional parent selector (P14-G8).
 *
 * @param {object}   props
 * @param {Array}    props.folders   Folder tree (for the parent dropdown)
 * @param {Function} props.onClose   Close without creating
 * @param {Function} props.onCreated Called with the new folder on success
 */
function NewFolderModal({ folders, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [parentId, setParentId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const options = flattenFolders(folders);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      const folder = await createFolder({
        name: name.trim(),
        parent_folder_id: parentId || null,
      });
      toast.success(`Folder "${folder.name}" created.`);
      onCreated(folder);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Failed to create folder.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-base font-semibold text-gray-900 mb-4">
          New Folder
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              className="block text-sm font-medium text-gray-700 mb-1"
              htmlFor="folder-name"
            >
              Name
            </label>
            <input
              id="folder-name"
              type="text"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Campaigns"
              maxLength={100}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label
              className="block text-sm font-medium text-gray-700 mb-1"
              htmlFor="folder-parent"
            >
              Parent folder{" "}
              <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <select
              id="folder-parent"
              value={parentId}
              onChange={(e) => setParentId(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">None (root)</option>
              {options.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !name.trim()}
              className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting ? "Creating…" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

NewFolderModal.propTypes = {
  folders: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func.isRequired,
};
NewFolderModal.defaultProps = { folders: [] };

export default NewFolderModal;
